/* ThisThat Catalogue V2 filtering engine
   Isolated from Dawn's normal facets so bespoke collections/search remain unchanged.
*/
(() => {
  if (!document.body.classList.contains('template-collection') && !document.body.classList.contains('template-search')) return;

  const cache = new Map();
  const CACHE_LIMIT = 10;
  let globalAbort = null;
  let debounceTimer = null;

  const setLoading = (loading) => {
    document.body.classList.toggle('facets-loading', loading);
    document.getElementById('ProductGridContainer')?.classList.toggle('loading', loading);
  };

  const remember = (url, html) => {
    if (cache.has(url)) cache.delete(url);
    cache.set(url, html);
    while (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
  };

  const swapInner = (doc, id) => {
    const src = doc.getElementById(id);
    const dst = document.getElementById(id);
    if (src && dst) dst.innerHTML = src.innerHTML;
  };

  const renderDocument = (doc, url) => {
    document.dispatchEvent(new CustomEvent('tt:facets:before-render'));
    swapInner(doc, 'ProductGridContainer');
    swapInner(doc, 'FacetsWrapperDesktop');
    swapInner(doc, 'FacetsWrapperMobile');
    swapInner(doc, 'CatalogueMobileApplyCount');
    swapInner(doc, 'CatalogueMobileHeaderCount');
    swapInner(doc, 'CatalogueMobileFilterBadge');
    history.replaceState({}, '', url);
    setLoading(false);
    document.dispatchEvent(new CustomEvent('tt:facets:rendered', { detail: { url } }));
    window.dispatchEvent(new Event('resize'));
  };

  const fetchAndRender = async (url) => {
    if (!url) return;
    const absolute = new URL(url, window.location.origin).toString();
    if (globalAbort) globalAbort.abort();
    globalAbort = new AbortController();
    setLoading(true);

    try {
      let html = cache.get(absolute);
      if (!html) {
        const response = await fetch(absolute, {
          credentials: 'same-origin',
          signal: globalAbort.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Catalogue request failed: ${response.status}`);
        html = await response.text();
        remember(absolute, html);
      }
      renderDocument(new DOMParser().parseFromString(html, 'text/html'), absolute);
    } catch (error) {
      if (error?.name === 'AbortError') {
        setLoading(false);
        return;
      }
      console.warn('[ThisThat] Catalogue refresh failed; falling back to navigation.', error);
      window.location.assign(absolute);
    }
  };

  window.TTCatalogueFacets = { fetchAndRender };

  class TTCatalogueFacetForm extends HTMLElement {
    connectedCallback() {
      if (this._ttBound) return;
      this._ttBound = true;
      this.form = this.querySelector('form');
      if (!this.form) return;

      this._onInput = (event) => {
        if (event.target.closest('[data-ttcat-price]')) return;
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => this.submitFromForm(), 70);
      };

      this._onClick = (event) => {
        const range = event.target.closest('[data-ttcat-range]');
        if (range) {
          event.preventDefault();
          const price = range.closest('[data-ttcat-price]');
          const from = price?.querySelector('input[data-ttcat-price-from]');
          const to = price?.querySelector('input[data-ttcat-price-to]');
          if (from) from.value = range.dataset.min || '';
          if (to) to.value = range.dataset.max || '';
          price?.querySelectorAll('[data-ttcat-range]').forEach((button) => button.classList.toggle('is-active', button === range));
          return;
        }

        const clearPrice = event.target.closest('[data-ttcat-price-clear]');
        if (clearPrice) {
          event.preventDefault();
          const price = clearPrice.closest('[data-ttcat-price]');
          price?.querySelectorAll('input[data-ttcat-price-from], input[data-ttcat-price-to]').forEach((input) => { input.value = ''; });
          price?.querySelectorAll('[data-ttcat-range]').forEach((button) => button.classList.remove('is-active'));
          this.submitFromForm();
          return;
        }

        const applyPrice = event.target.closest('[data-ttcat-price-apply]');
        if (applyPrice) {
          event.preventDefault();
          this.submitFromForm();
          return;
        }

        const remove = event.target.closest('facet-remove a, a[data-facet-remove]');
        if (remove) {
          event.preventDefault();
          fetchAndRender(remove.href);
        }
      };

      this.form.addEventListener('input', this._onInput, { passive: true });
      this.form.addEventListener('change', this._onInput, { passive: true });
      this.form.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      if (!this._ttBound || !this.form) return;
      this.form.removeEventListener('input', this._onInput);
      this.form.removeEventListener('change', this._onInput);
      this.form.removeEventListener('click', this._onClick);
      this._ttBound = false;
    }

    buildUrlFromForm() {
      const fd = new FormData(this.form);
      ['filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {
        if (fd.has(key)) fd.set(key, String(fd.get(key) || '').replace(/[^\d.]/g, ''));
      });
      const query = new URLSearchParams(fd).toString();
      const action = new URL(this.form.action || window.location.href, window.location.origin);
      const base = `${action.origin}${action.pathname}`;
      return query ? `${base}?${query}` : base;
    }

    submitFromForm() { fetchAndRender(this.buildUrlFromForm()); }
  }

  if (!customElements.get('tt-facet-filters-form')) {
    customElements.define('tt-facet-filters-form', TTCatalogueFacetForm);
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-facet-remove]');
    if (!link || link.closest('tt-facet-filters-form')) return;
    event.preventDefault();
    fetchAndRender(link.href);
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-tt-sort]');
    if (!select) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    fetchAndRender(url.toString());
  });
})();
